import { useState } from 'react'
import type { ThemePreference } from '../storage'
import { USERNAME_MAX_LENGTH } from '../nameValidation'
import DeleteAccountSheet from './DeleteAccountSheet'
import './ModalBackdrop.css'
import './Settings.css'

interface SettingsProps {
  displayName: string
  onSaveDisplayName: (displayName: string) => Promise<string | void>
  themePreference: ThemePreference
  onChangeTheme: (preference: ThemePreference) => void
  /** Null only briefly, before /me's initial load resolves — there's no anonymous/guest mode,
   * so Settings is never reachable without a signed-in account. */
  email: string | null
  /** The backoffice row is rendered only for an admin — and the screen behind it
   *  is a dynamic import, so a non-admin's browser never downloads the bundle either. */
  isAdmin?: boolean
  onOpenBackoffice?: () => void
  onLogout: () => void
  /** Runs after the server has confirmed the deletion — sign out, then reload. */
  onAccountDeleted: () => void
  onBack?: () => void
}

const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
]

function Settings({
  displayName,
  onSaveDisplayName,
  themePreference,
  onChangeTheme,
  email,
  isAdmin = false,
  onOpenBackoffice,
  onLogout,
  onAccountDeleted,
  onBack,
}: SettingsProps) {
  const [nameInput, setNameInput] = useState(displayName)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [showDeleteAccount, setShowDeleteAccount] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const hasUnsavedName =
    nameInput.trim() !== '' && nameInput.trim() !== displayName

  const handleNameChange = (value: string) => {
    setNameInput(value)
    setNameError(null)
  }

  const handleBack = () => {
    onBack?.()
  }


  const handleSave = async () => {
    if (!hasUnsavedName || isSaving) return
    setIsSaving(true)
    const error = await onSaveDisplayName(nameInput.trim())
    setIsSaving(false)
    if (error) {
      setNameError(error)
    }
  }

  return (
    <div className="settings-page">
      <div className="settings-page__header">
        {onBack && (
          <button
            type="button"
            className="settings-page__back"
            onClick={handleBack}
            aria-label="Back"
          >
            ← Back
          </button>
        )}
        <p className="settings-page__title">Settings</p>
      </div>

      <div className="settings-page__section">
        <label className="label-plain" htmlFor="settings-display-name">
          Display name
        </label>
        <div className="settings-page__name-row">
          <input
            id="settings-display-name"
            className="settings-page__input"
            value={nameInput}
            onChange={(e) => handleNameChange(e.target.value)}
            maxLength={USERNAME_MAX_LENGTH}
          />
          {hasUnsavedName && (
            <button
              type="button"
              className="settings-page__save-btn"
              onClick={handleSave}
              disabled={isSaving}
            >
              Save
            </button>
          )}
        </div>
        {nameError && <p className="settings-page__error">{nameError}</p>}
      </div>

      {email && (
        <div className="settings-page__section">
          <p className="label-plain">Account</p>
          <button
            type="button"
            className="settings-page__logout-btn"
            onClick={() => setShowLogoutConfirm(true)}
          >
            Log out
          </button>
        </div>
      )}

      {/* Below the account section: Settings already receives `email` and is the
          natural home for it. There is deliberately no entry point anywhere a non-admin can see,
          and no keyboard shortcut — a URL-less app has no deep link to leak. */}
      {isAdmin && onOpenBackoffice && (
        <div className="settings-page__section">
          <p className="label-plain">Admin</p>
          <button
            type="button"
            className="settings-page__logout-btn"
            onClick={onOpenBackoffice}
          >
            Open backoffice
          </button>
        </div>
      )}

      <div className="settings-page__section">
        <p className="label-plain">Appearance</p>
        <div className="settings-page__theme-row">
          {THEME_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`settings-page__theme-btn ${
                themePreference === option.value
                  ? 'settings-page__theme-btn--active'
                  : ''
              }`}
              onClick={() => onChangeTheme(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Last thing on the page, pushed to the foot of it — not a fourth row in the Account stack
. The tap target next to Log out is the one that must never be mistaken
          for it, and the surest way to prevent that is distance.

          A text link, not a filled crimson button. Crimson is the
          action-needed token, and this is not an action anyone needs — as a full-width fill it
          made the most destructive control the loudest thing on a screen whose job is editing a
          name. The weight belongs in the confirm flow, which is unchanged: two steps, the second
          gated on typing the account name. */}
      {/* The row itself is unconditional now so the legal links survive the moment
          before /me resolves; only the delete control waits for an email. Its `margin-top: auto`
          is what parks the whole row at the foot of the page. */}
      <div className="settings-page__danger">
        {email && (
          <button
            type="button"
            className="settings-page__danger-link"
            onClick={() => setShowDeleteAccount(true)}
          >
            Delete my account
          </button>
        )}
        {/*. Sharing the row rather than sitting under it: the delete link's isolation
            is about distance from Log out, and two links at the far end in the app's faintest text
            do not compete with it. New tab for the same reason as the sign-in screen's line —
            Settings is a screen, not a URL, so a same-tab navigation away cannot be navigated back
            to. */}
        <p className="settings-page__legal">
          <a href="/terms" target="_blank" rel="noopener">
            Terms
          </a>
          <a href="/privacy" target="_blank" rel="noopener">
            Privacy Policy
          </a>
        </p>
      </div>

      {showDeleteAccount && (
        <DeleteAccountSheet
          displayName={displayName}
          onCancel={() => setShowDeleteAccount(false)}
          onDeleted={onAccountDeleted}
        />
      )}

      {showLogoutConfirm && (
        <div className="modal-backdrop">
          <div className="modal-backdrop__content settings-page__confirm">
            <p className="settings-page__confirm-title">Log out?</p>
            <p className="settings-page__confirm-body">
              You'll be signed out of {email}. You can sign back in anytime.
            </p>
            <div className="settings-page__confirm-actions">
              <button
                type="button"
                className="settings-page__confirm-btn settings-page__confirm-btn--discard"
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="settings-page__confirm-btn settings-page__confirm-btn--logout"
                onClick={onLogout}
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Settings
